export interface CalendarEvent {
  title: string;
  startDateTime: string;
  endDateTime: string;
  description: string;
  calendarType: 'google' | 'yahoo' | 'ics' | undefined;
  videoCallUrl?: string;
}

/**
 * Formats a date string to the format required by calendar services (YYYYMMDDTHHmmssZ)
 * @param dateString - ISO date string
 * @returns Formatted date string in YYYYMMDDTHHmmssZ format
 */
const formatDateForCalendar = (dateString: string): string => {
  const date = new Date(dateString);

  return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
};

/**
 * Generates a Google Calendar URL with pre-filled event details
 * @param event - Calendar event details
 * @returns Google Calendar URL
 */
export const generateGoogleCalendarUrl = (event: CalendarEvent): string => {
  const baseUrl = 'https://calendar.google.com/calendar/render';

  let description = event.description;

  if (event.videoCallUrl) {
    description += `\n\nJoin via video call: ${event.videoCallUrl}`;
  }

  const params: Record<string, string> = {
    action: 'TEMPLATE',
    text: event.title,
    dates: `${formatDateForCalendar(event.startDateTime)}/${formatDateForCalendar(event.endDateTime)}`,
    details: description,
  };

  // Add location if videoCallUrl is provided
  if (event.videoCallUrl) {
    params.location = event.videoCallUrl;
  }

  return `${baseUrl}?${new URLSearchParams(params).toString()}`;
};

/**
 * Generates a Yahoo Calendar URL with pre-filled event details
 * @param event - Calendar event details
 * @returns Yahoo Calendar URL
 */
export const generateYahooCalendarUrl = (event: CalendarEvent): string => {
  const baseUrl = 'https://calendar.yahoo.com/';

  let description = event.description;

  if (event.videoCallUrl) {
    description += `\n\nJoin via video call: ${event.videoCallUrl}`;
  }

  // Calculate duration in hours and minutes
  // Yahoo Calendar uses 'dur' (duration) instead of 'et' (end time) to avoid timezone interpretation issues
  // Format: HHMM (e.g., '0200' for 2 hours, '0130' for 1 hour 30 minutes)
  const startDate = new Date(event.startDateTime);
  const endDate = new Date(event.endDateTime);
  const durationMinutes = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60));
  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;
  const duration =
    hours > 0
      ? `${String(hours).padStart(2, '0')}${String(minutes).padStart(2, '0')}`
      : String(minutes).padStart(4, '0');

  const params: Record<string, string> = {
    v: '60',
    view: 'd',
    type: '20',
    title: event.title,
    st: formatDateForCalendar(event.startDateTime),
    dur: duration,
    desc: description,
  };

  // Add location if videoCallUrl is provided
  if (event.videoCallUrl) {
    params.in_loc = event.videoCallUrl;
  }

  return `${baseUrl}?${new URLSearchParams(params).toString()}`;
};

/**
 * Generates and downloads an .ics file for calendar import
 * Compatible with Outlook, Apple Calendar, and other calendar applications
 * @param event - Calendar event details
 */
export const generateIcsFile = (event: CalendarEvent): void => {
  const startDate = formatDateForCalendar(event.startDateTime);
  const endDate = formatDateForCalendar(event.endDateTime);

  let description = event.description;

  if (event.videoCallUrl) {
    description += `\n\nJoin via video call: ${event.videoCallUrl}`;
  }

  const icsLines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Atlassian Learning//Live Training//EN',
    'BEGIN:VEVENT',
    `DTSTART:${startDate}`,
    `DTEND:${endDate}`,
    `SUMMARY:${event.title}`,
    `DESCRIPTION:${description.replace(/\n/g, '\\n')}`,
  ];

  // Add location if videoCallUrl is provided
  if (event.videoCallUrl) {
    icsLines.push(`LOCATION:${event.videoCallUrl}`);
  }

  icsLines.push('END:VEVENT', 'END:VCALENDAR');

  const icsContent = icsLines.join('\r\n');

  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');

  link.href = URL.createObjectURL(blob);
  link.download = `${event.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

/**
 * Adds an event to a calendar based on the calendar type
 * @param event - Calendar event details including the calendar type
 */
export const addToCalendar = (event: CalendarEvent): void => {
  try {
    switch (event.calendarType) {
      case 'google': {
        const googleUrl = generateGoogleCalendarUrl(event);

        window.open(googleUrl, '_blank', 'noopener,noreferrer');
        break;
      }
      case 'yahoo': {
        const yahooUrl = generateYahooCalendarUrl(event);

        window.open(yahooUrl, '_blank', 'noopener,noreferrer');
        break;
      }
      case 'ics':
        generateIcsFile(event);
        break;
      default:
        // eslint-disable-next-line no-console
        console.warn('Unknown calendar type:', event.calendarType);
        break;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error adding to calendar:', error);
  }
};
